<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import { reactive, ref } from "vue"
import { EyeIcon, Squares2X2Icon } from "@heroicons/vue/24/outline"
import AppShell from "../Layout/AppShell.vue"
import { UiButton, UiDataTable, UiDrawer } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"
import type { UiDataTableColumn, UiDataTableFilter } from "@/types/ui"
import BOMComponentTreeRows from "./components/BOMComponentTreeRows.vue"

const { confirm } = useConfirmation()
const notify = useNotifications()

type BomRow = {
  id: number
  company: string
  product: string
  unit: string
  components_count: number
  can_edit: boolean
  can_destroy: boolean
}

type BomTreeNode = {
  component: string
  quantity: string
  unit: string
  unit_cost: string
  line_cost: string
  children: BomTreeNode[]
}

type BomDetail = {
  estimated_unit_cost: string
  component_tree: BomTreeNode[]
}

const props = defineProps<{
  bill_of_materials: BomRow[]
  companies: Array<{ id: number; name: string }>
  query: {
    q?: string
    company_id?: string
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
  can_create: boolean
}>()

const columns: UiDataTableColumn[] = [
  { key: "company", label: "Empresa" },
  { key: "product", label: "Produto pai" },
  { key: "unit", label: "Unidade", align: "center" },
  { key: "components_count", label: "Componentes", align: "center" },
  { key: "actions", label: "Acoes", align: "right" },
]

const filters: UiDataTableFilter[] = [
  {
    key: "company_id",
    label: "Empresa",
    options: props.companies.map((company) => ({ label: company.name, value: company.id })),
  },
]

const selectedFilters = reactive<Record<string, string | number | "">>({
  company_id: props.query.company_id ?? "",
})

const isDetailsDrawerOpen = ref(false)
const selectedBom = ref<BomRow | null>(null)
const bomDetail = ref<BomDetail | null>(null)
const isLoadingDetail = ref(false)

async function openDetailsDrawer(bom: BomRow) {
  selectedBom.value = bom
  bomDetail.value = null
  isLoadingDetail.value = true
  isDetailsDrawerOpen.value = true

  try {
    const response = await fetch(`/bill_of_materials/${bom.id}/detail`, {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
      },
    })

    if (response.ok) {
      bomDetail.value = await response.json()
    }
  } finally {
    isLoadingDetail.value = false
  }
}

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    q: String(overrides.q ?? props.query.q ?? ""),
    company_id: String(overrides.company_id ?? selectedFilters.company_id ?? ""),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/bill_of_materials", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function updateFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  fetchWithQuery({ [key]: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

async function destroyBom(id: number) {
  const ok = await confirm({
    title: "Excluir composicao",
    message: "Deseja excluir esta estrutura?",
    type: "danger",
    confirmText: "Excluir",
  })
  if (!ok) return

  router.delete(`/bill_of_materials/${id}`, {
    preserveScroll: true,
    onSuccess: () => notify.success("Composicao do produto excluida com sucesso"),
  })
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Composicao do Produto</h1>
        <p class="mt-2 text-sm text-slate-600">Cadastre estruturas de produto e componha o custo estimado por unidade.</p>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="bill_of_materials"
        :search="query.q ?? ''"
        :filters="filters"
        :selected-filters="selectedFilters"
        :pagination="pagination"
        @update:search="updateSearch"
        @update:filter="updateFilter"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #toolbar-actions>
          <Link
            v-if="can_create"
            href="/bill_of_materials/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Nova composicao
          </Link>
        </template>

        <template #cell="{ row, column, value }">
          <div v-if="column.key === 'actions'" class="flex justify-end gap-2">
            <button
              type="button"
              class="rounded-md border border-slate-300 p-1.5 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              aria-label="Visualizar composicao do produto"
              @click="openDetailsDrawer(row as BomRow)"
            >
              <EyeIcon class="size-4" />
            </button>

            <Link
              v-if="(row as BomRow).can_edit"
              :href="`/bill_of_materials/${(row as BomRow).id}/edit`"
              class="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Editar
            </Link>

            <UiButton v-if="(row as BomRow).can_destroy" size="sm" variant="danger" @click="destroyBom((row as BomRow).id)">
              Excluir
            </UiButton>
          </div>

          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>

      <UiDrawer v-model="isDetailsDrawerOpen" title="Detalhes da composicao" size="2xl">
        <div v-if="selectedBom" class="space-y-4">
          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p class="text-xs uppercase tracking-wide text-slate-500">Estrutura de produto</p>
                <h3 class="text-lg font-semibold text-slate-900">{{ selectedBom.product }}</h3>
                <p class="mt-1 text-sm text-slate-600">Empresa: {{ selectedBom.company }}</p>
              </div>

              <span class="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                {{ selectedBom.components_count }} componentes diretos
              </span>
            </div>

            <div class="mt-4 grid gap-3 md:grid-cols-3">
              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Produto pai</p>
                <p class="mt-1 text-sm font-semibold text-slate-900">{{ selectedBom.product }}</p>
              </article>

              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Unidade</p>
                <p class="mt-1 text-sm font-semibold text-slate-900">{{ selectedBom.unit }}</p>
              </article>

              <article class="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                <p class="text-xs uppercase tracking-wide text-emerald-700">Custo estimado por unidade</p>
                <p v-if="isLoadingDetail" class="mt-1 text-sm text-slate-400">Carregando...</p>
                <p v-else-if="bomDetail" class="mt-1 text-lg font-semibold text-emerald-800">{{ bomDetail.estimated_unit_cost }}</p>
                <p v-else class="mt-1 text-sm text-slate-400">-</p>
              </article>
            </div>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p class="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Squares2X2Icon class="size-4 text-slate-500" /> Componentes em arvore
            </p>

            <div v-if="isLoadingDetail" class="py-6 text-center text-sm text-slate-400">
              Carregando componentes...
            </div>

            <div v-else-if="bomDetail" class="overflow-hidden rounded-md border border-slate-200">
              <table class="min-w-full divide-y divide-slate-200 text-sm">
                <thead class="bg-slate-50 text-slate-600">
                  <tr>
                    <th class="px-3 py-2 text-left font-medium">Componente</th>
                    <th class="px-3 py-2 text-right font-medium">Qtd</th>
                    <th class="px-3 py-2 text-center font-medium">Unidade</th>
                    <th class="px-3 py-2 text-right font-medium">Custo unit.</th>
                    <th class="px-3 py-2 text-right font-medium">Custo linha</th>
                  </tr>
                </thead>

                <tbody class="divide-y divide-slate-100 bg-white">
                  <BOMComponentTreeRows :nodes="bomDetail.component_tree" />

                  <tr v-if="bomDetail.component_tree.length === 0">
                    <td colspan="5" class="px-3 py-4 text-center text-slate-500">Sem componentes na estrutura.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </UiDrawer>
    </div>
  </AppShell>
</template>


